import { useState } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';

const ElementVisualPreview = ({ element, url, isPremium, onCapture }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [capturedData, setCapturedData] = useState(null);
  const [error, setError] = useState(null);

  const openInNewTab = (imageSrc) => {
    window.open(imageSrc, '_blank');
  };

  const hasVisuals = element.screenshot || element.contextScreenshot || element.extendedHTML;

  const handleCapture = async () => {
    if (!isPremium) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/lab-audits/cls-diagnostics/capture-element', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url, selector: element.selector })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setCapturedData(result.data);
        if (onCapture) onCapture(result.data);
      } else {
        setError(result.error || 'Failed to capture element');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const displayData = capturedData || element;
  const screenshot = displayData.screenshot;
  const contextScreenshot = displayData.contextScreenshot;
  const extendedHTML = displayData.extendedHTML || element.snippet;

  return (
    <div className="mt-2 border border-slate-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-2 bg-slate-800/50 hover:bg-slate-800/70 transition-colors"
      >
        <div className="flex items-center gap-2 text-xs">
          <Icons.Eye size={12} className="text-purple-400" />
          <span className="text-slate-300">
            {language === 'cs' ? 'Vizuální náhled' : 'Visual Preview'}
          </span>
          {hasVisuals && (
            <span className="px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 text-[10px]">
              {language === 'cs' ? 'K dispozici' : 'Available'}
            </span>
          )}
        </div>
        <Icons.ChevronDown size={12} className={`text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="p-3 bg-slate-900/50 space-y-3">
          {contextScreenshot && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-2">
                <Icons.Monitor size={10} />
                {language === 'cs' ? 'Kontext na stránce' : 'Page Context'}
                <span className="text-purple-400 ml-auto flex items-center gap-1 cursor-pointer hover:text-purple-300" onClick={() => openInNewTab(contextScreenshot)}>
                  <Icons.ExternalLink size={10} />
                  {language === 'cs' ? 'Otevřít v nové záložce' : 'Open in new tab'}
                </span>
              </div>
              <div 
                className="rounded-lg overflow-hidden border border-slate-700 bg-slate-950 cursor-pointer hover:border-purple-500/50 transition-colors"
                onClick={() => openInNewTab(contextScreenshot)}
              >
                <img 
                  src={contextScreenshot} 
                  alt="Element in page context" 
                  className="w-full max-h-48 object-contain"
                  onError={(e) => {
                    e.target.parentElement.style.display = 'none';
                  }}
                />
              </div>
              <div className="text-[10px] text-slate-500 mt-1 text-center">
                {language === 'cs' ? 'Červený rámeček označuje problematický element' : 'Red outline marks the problematic element'}
              </div>
            </div>
          )}

          {screenshot ? (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-2">
                <Icons.ZoomIn size={10} />
                {language === 'cs' ? 'Detail elementu' : 'Element Close-up'}
                <span className="text-purple-400 ml-auto flex items-center gap-1 cursor-pointer hover:text-purple-300" onClick={() => openInNewTab(screenshot)}>
                  <Icons.ExternalLink size={10} />
                  {language === 'cs' ? 'Otevřít v nové záložce' : 'Open in new tab'}
                </span>
              </div>
              <div 
                className="rounded-lg overflow-hidden border border-slate-700 bg-slate-950 cursor-pointer hover:border-purple-500/50 transition-colors"
                onClick={() => openInNewTab(screenshot)}
              >
                <img 
                  src={screenshot} 
                  alt="Element screenshot" 
                  className="max-w-full max-h-48 object-contain mx-auto"
                  onError={(e) => {
                    e.target.parentElement.style.display = 'none';
                  }}
                />
              </div>
            </div>
          ) : !loading && isPremium && !contextScreenshot && (
            <button
              onClick={handleCapture}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 transition-colors"
            >
              <Icons.Camera size={14} />
              <span className="text-xs">
                {language === 'cs' ? 'Zachytit vizuální náhled' : 'Capture Visual Preview'}
              </span>
            </button>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 p-4 text-slate-400">
              <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs">
                {language === 'cs' ? 'Zachytávám element...' : 'Capturing element...'}
              </span>
            </div>
          )}

          {(error || element.captureError) && (
            <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
              <Icons.AlertTriangle size={12} className="inline mr-1" />
              {error || (language === 'cs' 
                ? 'Nepodařilo se zachytit element: ' + element.captureError
                : 'Failed to capture element: ' + element.captureError)}
            </div>
          )}

          {extendedHTML && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-2">
                <Icons.Code size={10} />
                {language === 'cs' ? 'HTML kód' : 'HTML Code'}
              </div>
              <pre className="p-2 rounded-lg bg-slate-950 border border-slate-800 text-[10px] text-cyan-300 font-mono overflow-x-auto max-h-32 overflow-y-auto whitespace-pre-wrap break-all">
                {extendedHTML}
              </pre>
            </div>
          )}

          {displayData.computedDimensions && (
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 rounded-lg bg-slate-800/50">
                <div className="text-[10px] text-slate-500 mb-1">
                  {language === 'cs' ? 'Rozměry' : 'Dimensions'}
                </div>
                <div className="text-xs text-slate-300">
                  {displayData.computedDimensions.width} × {displayData.computedDimensions.height}
                </div>
              </div>
              {displayData.boundingRect && (
                <div className="p-2 rounded-lg bg-slate-800/50">
                  <div className="text-[10px] text-slate-500 mb-1">
                    {language === 'cs' ? 'Pozice' : 'Position'}
                  </div>
                  <div className="text-xs text-slate-300">
                    Top: {Math.round(displayData.boundingRect.top)}px
                  </div>
                </div>
              )}
            </div>
          )}

          {!isPremium && (
            <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs text-center">
              <Icons.Lock size={12} className="inline mr-1" />
              {language === 'cs' 
                ? 'Vizuální náhled je prémiová funkce'
                : 'Visual preview is a premium feature'}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default ElementVisualPreview;
